import { AddExistingGuests } from '@common/components/AddExistingGuests';
import { useCallback, useRef } from 'react';
import { UseFieldArrayReturn, useFormContext } from 'react-hook-form';
import { Guest } from 'shared-types/Basket';
import { SpaEntityFormValues } from '../SpaEntityForm.schema';

type GuestFieldArray = UseFieldArrayReturn<SpaEntityFormValues, 'guests', 'id'>;
type GuestEntry = SpaEntityFormValues['guests'][number];

type SpaEntityAddExistingGuestsV2Props = {
  fieldArray: GuestFieldArray;
  guestValues: SpaEntityFormValues['guests'];
  selectedGuests: Guest[];
  existingGuests: Guest[];
  guest: Guest | null;
  minimumGuests?: number | null;
  maximumGuests?: number | null;
  isGenderSpecific?: string | boolean;
};

export const SpaEntityAddExistingGuestsV2 = ({
  fieldArray,
  guestValues,
  selectedGuests,
  existingGuests,
  guest,
  minimumGuests,
  maximumGuests,
  isGenderSpecific = false,
}: SpaEntityAddExistingGuestsV2Props) => {
  const { append, remove, insert } = fieldArray;
  const { trigger } = useFormContext<SpaEntityFormValues>();
  const pendingDelta = useRef(0);

  const triggerGuests = useCallback(() => {
    setTimeout(() => {
      pendingDelta.current = 0;
      trigger('guests');
    }, 0);
  }, [trigger]);

  const currentLength = (guestValues?.length ?? 0) + pendingDelta.current;

  const fields = {
    push: (value: GuestEntry) => {
      append(value);
      pendingDelta.current += 1;
      triggerGuests();
    },
    remove: (index: number) => {
      remove(index);
      pendingDelta.current -= 1;
      triggerGuests();
    },
    insert: (index: number, value: GuestEntry) => {
      insert(index, value);
      pendingDelta.current += 1;
      triggerGuests();
    },
    get: (index: number) => guestValues?.[index],
    getAll: () => guestValues ?? [],
    get length() {
      return currentLength;
    },
    pop: () => {
      const len = guestValues?.length ?? 0;
      if (len > 0) {
        remove(len - 1);
        pendingDelta.current -= 1;
        triggerGuests();
      }
    },
  };

  return (
    <AddExistingGuests
      fields={fields}
      selectedGuests={selectedGuests}
      existingGuests={existingGuests}
      guest={guest as Guest}
      minimumGuests={minimumGuests}
      maximumGuests={maximumGuests}
      isGenderSpecific={isGenderSpecific}
    />
  );
};
